/** Token storage and user info utilities for WorkOS auth. */

const ACCESS_TOKEN_KEY = 'workos_access_token'
const REFRESH_TOKEN_KEY = 'workos_refresh_token'
const USER_KEY = 'workos_user'

export interface AuthUser {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function storeSession(accessToken: string, user: AuthUser, refreshToken?: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem(USER_KEY, JSON.stringify(user))
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  }
}

export function clearSession(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

// ── Intentional-logout flag ──────────────────────────────────────────────────
// One-shot sessionStorage marker, set by logout() ONLY when it could not
// complete a WorkOS hosted logout (token carried no session id, or /auth/logout
// was unreachable). In that case WorkOS's own SSO cookie may still be live, so
// App reads this to render an explicit "Sign in" screen instead of
// auto-redirecting to /authorize — which would silently re-authenticate the
// same identity (the "had to click logout twice" bug). Scoped to the tab and
// cleared the moment the user chooses to sign in again.
const INTENTIONAL_LOGOUT_KEY = 'pt_intentional_logout'

export function markIntentionalLogout(): void {
  try {
    sessionStorage.setItem(INTENTIONAL_LOGOUT_KEY, '1')
  } catch {
    // sessionStorage can throw in hardened privacy modes — non-fatal.
  }
}

export function hasIntentionalLogout(): boolean {
  try {
    return sessionStorage.getItem(INTENTIONAL_LOGOUT_KEY) === '1'
  } catch {
    return false
  }
}

export function clearIntentionalLogout(): void {
  try {
    sessionStorage.removeItem(INTENTIONAL_LOGOUT_KEY)
  } catch {
    // non-fatal
  }
}
